import React from "react";
import moment from "moment";
import { safeText } from "../utils/hubHelpers";

const FUEL_ICONS = {
  Diesel: { icon: "🛢️", label: "Diesel", color: "#4f46e5", bg: "#eef2ff" },
  Petrol: { icon: "⛽", label: "Petrol", color: "#d97706", bg: "#fef3c7" },
  Electric: { icon: "⚡", label: "Electric", color: "#059669", bg: "#ecfdf5" },
  CNG: { icon: "🔵", label: "CNG", color: "#2563eb", bg: "#eff6ff" },
  Hybrid: { icon: "🔋", label: "Hybrid", color: "#0d9488", bg: "#f0fdfa" },
};

export default function AssetCard({
  item,
  onInspect,
  onLogReading,
  onChangeStatus,
  onAddService,
  onEdit,
  onTransfer,
}) {
  const isBreakdown = item?.status === "Breakdown";
  const isUnderService = item?.status === "Under Service" || item?.status === "In Service";
  const isIdle = item?.status === "Idle";

  const statusClass = isBreakdown
    ? "status-breakdown"
    : isUnderService
    ? "status-service"
    : isIdle
    ? "status-idle"
    : "status-operational";

  const statusLabel = safeText(item?.status, "Operational");
  const fuelTypeStr = typeof item?.fuelType === "string" ? item.fuelType : "";
  const fuelMeta = FUEL_ICONS[fuelTypeStr] || { icon: "⚙️", label: safeText(item?.fuelType || item?.type, "Standard"), color: "#64748b", bg: "#f1f5f9" };

  // Calculate overdue or upcoming service
  let isServiceOverdue = false;
  let serviceStatusText = null;
  if (item?.nextServiceDate) {
    const nextDate = moment(item.nextServiceDate);
    const today = moment();
    const diffDays = nextDate.diff(today, "days");

    if (diffDays < 0) {
      isServiceOverdue = true;
      serviceStatusText = `Service ${Math.abs(diffDays)}d overdue`;
    } else if (diffDays <= 45) {
      serviceStatusText = `Service in ${diffDays}d`;
    }
  }

  // Document expiry status if available
  const hasDocExpired = Array.isArray(item?.Documents) && item.Documents.some((doc) => {
    return doc?.expiryDate && moment(doc.expiryDate).isBefore(moment());
  });

  return (
    <div className="machinery-asset-card">
      {/* Top Header */}
      <div className="card-top-bar">
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", minWidth: 0 }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 10,
              background: fuelMeta.bg,
              color: fuelMeta.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.35rem",
              flexShrink: 0,
              border: `1px solid ${fuelMeta.color}30`,
            }}
          >
            {fuelMeta.icon}
          </div>
          <div style={{ minWidth: 0, overflow: "hidden" }}>
            <h4 className="asset-primary-title" title={safeText(item?.name, "Equipment")}>
              {safeText(item?.name, "Equipment")}
            </h4>
            <div className="asset-sub-meta">
              <span className="asset-tag-badge">{safeText(item?.assetNo, "ASSET-N/A")}</span>
              {item?.model && <span>• {safeText(item.model)}</span>}
            </div>
          </div>
        </div>

        <span className={`status-badge ${statusClass}`}>
          <span style={{ fontSize: "9px" }}>●</span> {statusLabel}
        </span>
      </div>

      {/* Specs Matrix */}
      <div className="card-specs-matrix">
        <div className="spec-item">
          <span className="spec-label">Site Location</span>
          <span className="spec-value text-truncate" title={safeText(item?.Site?.name || item?.Site)}>
            📍 {safeText(item?.Site?.name || item?.Site, "Unassigned")}
          </span>
        </div>

        <div className="spec-item">
          <span className="spec-label">Registration Plate</span>
          <span className="spec-value" style={{ fontFamily: "monospace", letterSpacing: "0.03em" }}>
            {safeText(item?.machineNumber, "—")}
          </span>
        </div>

        <div className="spec-item">
          <span className="spec-label">Chassis / Serial</span>
          <span className="spec-value text-truncate" style={{ fontFamily: "monospace", fontSize: "12px" }}>
            {safeText(item?.chesisNumber || item?.engineNumber, "—")}
          </span>
        </div>

        <div className="spec-item">
          <span className="spec-label">PO / PT Link</span>
          <span className="spec-value" style={{ color: "#2563eb", fontWeight: 700 }}>
            {safeText(item?.ptNumber || item?.poNumber, "Direct Added")}
          </span>
        </div>
      </div>

      {/* Compliance / Status Pills */}
      <div className="card-compliance-pills">
        {isServiceOverdue ? (
          <span className="compliance-pill pill-danger">
            ⚠️ {serviceStatusText}
          </span>
        ) : serviceStatusText ? (
          <span className="compliance-pill pill-warning">
            🔧 {serviceStatusText}
          </span>
        ) : (
          <span className="compliance-pill pill-success">
            ✓ Health: Verified Operational
          </span>
        )}

        {hasDocExpired && (
          <span className="compliance-pill pill-danger">
            📄 Doc Expired
          </span>
        )}
      </div>

      {/* Action Footer */}
      <div className="card-actions-footer">
        <div style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap" }}>
          <button
            type="button"
            className="action-pill-btn"
            onClick={() => onLogReading?.(item)}
            title="Log Hour / KM Reading"
          >
            ⏱ Meter
          </button>
          <button
            type="button"
            className="action-pill-btn"
            onClick={() => onChangeStatus?.(item)}
            title="Update Machine Status"
          >
            ⚡ Status
          </button>
          <button
            type="button"
            className="action-pill-btn"
            onClick={() => onAddService?.(item)}
            title="Log Service / Repair"
          >
            🔧 Service
          </button>
          <button
            type="button"
            className="action-pill-btn"
            onClick={() => onTransfer?.(item)}
            title="Transfer Machine to Another Site"
            style={{ background: "#eff6ff", color: "#2563eb", borderColor: "#bfdbfe" }}
          >
            🔁 Transfer
          </button>
          <button
            type="button"
            className="action-pill-btn"
            onClick={() => onEdit?.(item)}
            title="Edit Machine Specifications"
            style={{ background: "#f8fafc", color: "#475569" }}
          >
            ✏️ Edit
          </button>
        </div>

        <button
          type="button"
          className="action-pill-btn btn-inspect"
          onClick={() => onInspect?.(item)}
        >
          Open ↗
        </button>
      </div>
    </div>
  );
}
